import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, XCircle } from "lucide-react";

import { validateSession } from "@/services/auth.service";
import { useSyncPolling, formatStepMessage } from "@/hooks/use-sync-polling";
import { useAppToast } from "@/contexts/ToastContext";
import posthog from "@/lib/posthog";

const SyncingData = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const syncStatusId = location.state?.syncStatusId as string | undefined;
  const { showToast } = useAppToast();

  const {
    currentStep,
    completedSteps,
    displayedProgress,
    isComplete,
    error,
    startPolling,
    stopPolling,
  } = useSyncPolling("Preparing to sync...", "INITIAL");

  // Validate session and check sync state on mount
  useEffect(() => {
    let cancelled = false;

    const checkSession = async () => {
      const sessionData = await validateSession();
      if (cancelled) return;
      if (!sessionData) {
        navigate("/", { replace: true });
        return;
      }
      if (!sessionData.hasIntegration) {
        navigate("/onboarding/connect", { replace: true });
        return;
      }

      startPolling(syncStatusId).catch(() => {});
    };

    checkSession();

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [navigate, startPolling, stopPolling, syncStatusId]);

  // Auto-redirect on sync completion
  useEffect(() => {
    if (!isComplete) return;
    posthog.capture("initial_sync_completed");
    const timer = setTimeout(() => {
      navigate("/dashboard", {
        replace: true,
        state: { syncJustCompleted: true },
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [isComplete, navigate]);

  // Show error toast and navigate on sync failure
  useEffect(() => {
    if (!error) return;
    const handleError = async () => {
      const sessionData = await validateSession().catch(() => null);
      if (sessionData?.integrationStatus === 'error') {
        navigate("/onboarding/connect", { replace: true });
      } else {
        showToast({
          type: "error",
          title: "Sync Failed",
          message: error,
          autoHide: false,
        });
        navigate("/dashboard", { replace: true });
      }
    };
    handleError();
  }, [error, navigate, showToast]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <Card className="border-0 shadow-xl bg-card/95 backdrop-blur-sm">
          <CardContent className="p-8">
            <div className="mb-8 text-center">
              <div className="w-20 h-20 mx-auto mb-4">
                <img
                  src="/content/logo.png"
                  alt="BlinkBooks Logo"
                  className="w-full h-full object-contain"
                />
              </div>

              <>
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  {isComplete
                    ? "Your data is ready!"
                    : "We're importing your financial data"}
                </h1>
                <p className="text-muted-foreground mb-6">
                  {isComplete
                    ? "Redirecting you to your dashboard..."
                    : "This usually takes 1\u20133 minutes"}
                </p>

                <div className="space-y-2 mb-6">
                  <Progress value={displayedProgress} className="h-3" />
                  <p className="text-sm text-muted-foreground">
                    {displayedProgress}% &mdash; {currentStep}
                  </p>
                </div>

                {completedSteps.length > 0 && (
                  <div className="text-left space-y-2">
                    {completedSteps.map((step) => (
                      <div
                        key={step.entity}
                        className={`flex items-center gap-2 text-sm ${step.error ? 'text-red-500' : 'text-green-600'}`}
                      >
                        {step.error
                          ? <XCircle className="w-4 h-4 flex-shrink-0" />
                          : <CheckCircle2 className="w-4 h-4 flex-shrink-0" />}
                        <span>{formatStepMessage(step)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SyncingData;
